import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'react-qr-code';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from "@/hooks/ui/use-toast";
import { Loader2, RefreshCw } from 'lucide-react';

interface QrTokenData {
  token: string;
  expires_at: string;
}

interface EmployeeArrivalQrCodeProps {
  employeeId: string;
  businessId: string;
}

const EmployeeArrivalQrCode: React.FC<EmployeeArrivalQrCodeProps> = ({ employeeId, businessId }) => {
  const { session } = useAuth();
  const [qrToken, setQrToken] = useState<QrTokenData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(5); // Countdown for refresh

  const fetchQrToken = useCallback(async () => {
    if (!session?.access_token) {
      setError("User not authenticated. Please log in.");
      return;
    }

    setError(null);
    setTimeLeft(5); // Reset countdown on new fetch
    try {
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch('/functions/v1/generate-qr-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY, // Include the anon key for direct function calls
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate QR token');
      }

      const data: QrTokenData = await response.json();
      setQrToken(data);
    } catch (err: any) {
      console.error("Error fetching QR token:", err);
      setError(err.message || "Failed to generate QR code. Please try again.");
      setQrToken(null);
      toast.error("Failed to generate QR code.");
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchQrToken(); // Initial fetch

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          fetchQrToken();
          return 5; // Reset for next cycle
        }
        return prevTime - 1;
      });
    }, 1000); // Update every second

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [fetchQrToken]);

  const qrCodeValue = qrToken ? `${window.location.origin}/scan-arrival?token=${qrToken.token}` : '';

  return (
    <Card className="w-full max-w-md mx-auto text-center">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">Scan for Arrival</CardTitle>
        <CardDescription>
          Present this QR code to the scanner to record your arrival time.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center p-6">
        {error ? (
          <div className="text-red-500 text-sm mb-4">
            <p>{error}</p>
            <Button onClick={fetchQrToken} variant="outline" className="mt-2">
              <RefreshCw className="h-4 w-4 mr-2" /> Retry
            </Button>
          </div>
        ) : qrToken ? (
          <>
            <div className="p-4 bg-white rounded-lg shadow-md">
              <QRCode
                value={qrCodeValue}
                size={200}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#000000"
                className="h-auto max-w-full"
              />
            </div>
          </>
        ) : (
          <div className="text-gray-500 text-sm">
            No QR code available.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeArrivalQrCode;
